import {inject, Injector, runInInjectionContext}                    from '@angular/core';
import {FirebasePaths, GlobalRecordBaseIntf, GlobalRecordGroupDict} from '@djsharkk/logr-types';
import {DateTime, Interval}                                         from 'luxon';
import {BehaviorSubject, map, Observable}                           from 'rxjs';
import {DataFactoryBase}                                            from '../data-factory.base';


export abstract class DataFirebaseBase<LOG extends GlobalRecordBaseIntf, SUM extends GlobalRecordBaseIntf | undefined = undefined> extends DataFactoryBase {

	injector = inject(Injector)

	protected abstract paths: GlobalRecordGroupDict<FirebasePaths, FirebasePaths | undefined>;
	protected records: GlobalRecordGroupDict<BehaviorSubject<LOG[]>, BehaviorSubject<SUM[]>> = {
		log: new BehaviorSubject<LOG[]>([]),
		sum: new BehaviorSubject<SUM[]>([])
	};


	async initializeData(todayOnly = false): Promise<void> {
		await runInInjectionContext(this.injector, async () => {
			this.svcFirebaseTools.recFetch<LOG>(
				this.paths.log,
				'timestamp' as keyof LOG,
				todayOnly ? DateTime.now().startOf('day').minus({day: 2}).toJSDate() : undefined
			).subscribe(
				{
					next : (data) => this.records.log.next(data),
					error: (err) => console.error(`Error fetching ${this.paths.log}:`, err)
				}
			);


			if (this.paths.sum) {
				this.svcFirebaseTools.recFetch<SUM>(
					this.paths.sum as FirebasePaths,
					'timestamp' as keyof SUM
				).subscribe({
								next : (data) => this.records.sum?.next(data),
								error: (err) => console.error(`Error fetching ${this.paths.sum}:`, err)
							});
			}
		});

	}

	getRecordsLog = () => this.records.log;
	getRecordsSum = () => this.records.sum;


	getRecordsLogToday = () => this.getRecordsLogDates([DateTime.now().startOf('day').toJSDate()]);
	getRecordsLogDates = (dates: Date[]): Observable<LOG[]> => this.records.log
																   .asObservable()
																   .pipe(
																	   map(records => this.filterObjectsByDateRange(records, dates))
																   )


	filterObjectsByDateRange<T extends GlobalRecordBaseIntf>(data: T[], dates: Date[]): T[] {
		if (!dates || dates.length === 0) {
			return [];
		}

		let minDate: DateTime
		let maxDate: DateTime

		if (dates.length === 1) {
			minDate = DateTime.fromJSDate(dates[0]).startOf('day')
			maxDate = DateTime.fromJSDate(dates[0]).endOf('day')
		} else {
			minDate = DateTime.fromJSDate(dates.reduce((prev, curr) => (prev < curr ? prev : curr))).startOf('day')
			maxDate = DateTime.fromJSDate(dates.reduce((prev, curr) => (prev > curr ? prev : curr))).endOf('day')
		}

		return data.filter((obj) => {
			const dateValue = obj?.timestamp;
			if (!dateValue) {
				return false
			}
			const objDate: DateTime = DateTime.fromMillis(dateValue.toMillis());
			const interval = Interval.fromDateTimes(minDate, maxDate);

			return interval.contains(objDate);
		})
	}


}